import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

export const Contatti = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Messaggio inviato! Ti risponderò al più presto.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contatti" data-testid="contatti-section" className="py-16 sm:py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-orange-400">Contatti</span>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
            Parliamone: il primo passo è una conversazione
          </h2>
        </motion.div>
        <div className="mt-12 max-w-3xl">
          <div>
            <div className="space-y-4">
              {[
                { icon: Mail, label: "Email", value: "info@giocalatuapartitamigliore.it", href: "mailto:info@giocalatuapartitamigliore.it", testid: "contact-email-link" },
                { icon: Phone, label: "Telefono", value: "+39 345 016 7359", href: "tel:+393450167359", testid: "contact-phone-link" },
                { icon: MapPin, label: "Dove", value: "Online e in presenza (Italia)", testid: "contact-location" },
              ].map((c) => (
                <div key={c.label} className="flex items-center gap-4 bg-[#10141D] border border-[#222A3B] rounded-2xl p-5 hover:border-sky-500/40 transition-colors">
                  <div className="w-11 h-11 rounded-xl bg-sky-500/10 border border-sky-500/30 flex items-center justify-center shrink-0">
                    <c.icon className="w-5 h-5 text-sky-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">{c.label}</p>
                    {c.href ? (
                      <a href={c.href} data-testid={c.testid} className="text-white font-medium hover:text-sky-400 transition-colors">{c.value}</a>
                    ) : (
                      <p data-testid={c.testid} className="text-white font-medium">{c.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={submit} className="mt-8 bg-[#10141D] border border-[#222A3B] rounded-2xl p-6 space-y-4">
              <h3 className="font-heading font-semibold text-white text-lg">Scrivimi un messaggio</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ct-name" className="text-slate-300">Nome *</Label>
                  <Input id="ct-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")}
                    className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500" />
                </div>
                <div>
                  <Label htmlFor="ct-email" className="text-slate-300">Email *</Label>
                  <Input id="ct-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")}
                    className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500" />
                </div>
              </div>
              <div>
                <Label htmlFor="ct-msg" className="text-slate-300">Messaggio *</Label>
                <Textarea id="ct-msg" data-testid="contact-message-input" required value={form.message} onChange={set("message")}
                  placeholder="Come posso aiutarti?" className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500 min-h-[100px]" />
              </div>
              <Button type="submit" data-testid="contact-form-submit" disabled={loading}
                className="bg-sky-400 text-[#050B14] hover:bg-sky-300 font-semibold">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Invia messaggio</>}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
